/**
 * Date range helpers for Bingo category active / blackout dates.
 *
 * - API datetime values are treated as exact UTC timestamps
 * - Converted to JS Date (local) but compared using exact time (ms)
 * - NO calendar-day normalization for datetime values
 *
 * - YYYY-MM-DD values (if ever used) -> treated as full local days
 */

/**
 * Parse YYYY-MM-DD -> local midnight
 */
function parseLocalDateOnly(str) {
  const m = String(str).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;

  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    0, 0, 0, 0
  );
}

/**
 * Parse API datetime (UTC string) -> JS Date
 */
function parseUtcDateTime(str) {
  const s = String(str).trim();
  if (!s) return null;

  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
  let normalized = s.replace(' ', 'T');

  if (!/[Tt]/.test(normalized)) {
    normalized = `${normalized}T00:00:00.000Z`;
  } else if (!hasTz) {
    normalized = `${normalized}Z`;
  }

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Get range bounds in milliseconds
 */
function getRangeBoundsMs(startStr, endStr) {
  // Case 1: Pure YYYY-MM-DD -> treat as full local day
  const startLocal = parseLocalDateOnly(startStr);
  const endLocal = parseLocalDateOnly(endStr);

  if (startLocal && endLocal) {
    const endExclusive = new Date(
      endLocal.getFullYear(),
      endLocal.getMonth(),
      endLocal.getDate() + 1,
      0, 0, 0, 0
    );

    return {
      startMs: startLocal.getTime(),
      endMs: endExclusive.getTime()
    };
  }

  // Case 2: API datetime -> exact timestamps
  const start = parseUtcDateTime(startStr);
  const end = parseUtcDateTime(endStr);

  if (!start || !end) return null;

  return {
    startMs: start.getTime(),
    endMs: end.getTime()
  };
}

/**
 * Check if a date is inside range
 */
export function isDateInRange(date, startStr, endStr) {
  const bounds = getRangeBoundsMs(startStr, endStr);
  if (!bounds) return false;

  const { startMs, endMs } = bounds;

  if (endMs <= startMs) return false;

  const t = date.getTime();

  return t >= startMs && t < endMs;
}

export function isInAnyDateRange(date, ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) return false;

  return ranges.some(
    r => r != null && isDateInRange(date, r.start_date, r.end_date)
  );
}

/**
 * Availability logic
 */
function isLocalDayAvailable(dayDate, activeDates, blackoutDates) {
  const active = Array.isArray(activeDates) ? activeDates : [];
  const blackout = Array.isArray(blackoutDates) ? blackoutDates : [];

  // Blackout always wins
  if (isInAnyDateRange(dayDate, blackout)) return false;

  // No active restriction -> allowed
  if (active.length === 0) return true;

  return isInAnyDateRange(dayDate, active);
}

/**
 * Get next available date (returns YYYY-MM-DD)
 */
export function getNextAvailableDate(activeDates, blackoutDates, now) {
  const ref = now instanceof Date ? now : new Date();

  for (let i = 1; i <= 800; i += 1) {
    const d = new Date(
      ref.getFullYear(),
      ref.getMonth(),
      ref.getDate() + i,
      12, 0, 0, 0 // midday to avoid edge cases
    );

    if (isLocalDayAvailable(d, activeDates, blackoutDates)) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${y}-${m}-${day}`;
    }
  }

  return null;
}

export function getNextAvailableDateTime(activeDates, blackoutDates, now) {
  const ref = now instanceof Date ? now : new Date();
  const active = Array.isArray(activeDates) ? activeDates : [];
  const blackout = Array.isArray(blackoutDates) ? blackoutDates : [];

  const candidates = [];

  active.forEach(r => {
    if (!r) return;
    const start = parseUtcDateTime(r.start_date);
    if (start && start.getTime() > ref.getTime()) candidates.push(start);
  });

  blackout.forEach(r => {
    if (!r) return;
    const end = parseUtcDateTime(r.end_date);
    if (end && end.getTime() > ref.getTime()) candidates.push(end);
  });

  candidates.sort((a, b) => a.getTime() - b.getTime());

  for (let i = 0; i < candidates.length; i += 1) {
    const t = candidates[i];

    // Must not be in blackout at that moment
    if (isInAnyDateRange(t, blackout)) continue;

    // If there are active ranges, it must be inside at that moment
    if (active.length > 0 && !isInAnyDateRange(t, active)) continue;

    return t;
  }

  return null;
}

export function formatNextAvailableMessage(nextDate) {
  if (!nextDate) return 'Currently unavailable';

  const now = new Date();

  const isSameDay =
    now.getFullYear() === nextDate.getFullYear() &&
    now.getMonth() === nextDate.getMonth() &&
    now.getDate() === nextDate.getDate();

  if (isSameDay) {
    const diffMs = nextDate.getTime() - now.getTime();
    const minutes = Math.round(diffMs / (1000 * 60));

    if (minutes <= 1) return 'Available in a moment';
    if (minutes < 60)
      return `Available in ${minutes} minute${minutes > 1 ? 's' : ''}`;

    const hours = Math.round(minutes / 60);
    return `Available in ${hours} hour${hours > 1 ? 's' : ''}`;
  }

  return `Available on ${nextDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  })}`;
}

/**
 * Lookup helper
 */
export function lookupCategoryActiveDatesEntry(map, categoryId) {
  if (map == null || categoryId == null) return undefined;

  const s = String(categoryId);
  if (Object.prototype.hasOwnProperty.call(map, s)) return map[s];

  const n = Number(categoryId);
  if (!Number.isNaN(n) && Object.prototype.hasOwnProperty.call(map, n)) {
    return map[n];
  }

  return undefined;
}